import React, {useMemo, useRef, useState} from "react";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Mention} from "primereact/mention";
import {OverlayPanel} from "primereact/overlaypanel";
import Avatar from "../../system/components/avatar";
import Icon from "../../system/components/icon";
import TwemojiText from "../../system/components/twemoji-text";
import {useTranslate} from "../../system/useTranslate";
import {EMOJI_CATEGORIES} from "./emoji-catalog";
import {richTextToPlain} from "../../system/components/rich-text";
import {FormatToolbar, applyFormat, formatShortcut} from "./text-formatting";

export const CHAT_REFERENCE_CONFIG = {
    task: {labelKey: "inbox.composer.action.task", icon: "mi mi:check_circle", color: "text-blue-600"},
    project: {labelKey: "inbox.composer.action.project", icon: "mi mi:folder_open", color: "text-orange-600"},
    deliverable: {labelKey: "inbox.composer.action.deliverable", icon: "mi mi:flag", color: "text-purple-600"},
    goal: {labelKey: "inbox.composer.action.goal", icon: "mi mi:track_changes", color: "text-green-600"},
    team: {labelKey: "inbox.composer.action.team", icon: "mi mi:groups", color: "text-cyan-600"},
    customer: {labelKey: "inbox.composer.action.customer", icon: "mi mi:person", color: "text-pink-600"},
};

const QUICK_ACTION_TYPES = ["task", "deliverable", "goal", "project", "team", "mention"];
const ICON_BUTTON_CLASS = "p-0 flex align-items-center justify-content-center flex-shrink-0 gap-2";
const CLICKABLE_ICON_CLASS = "line-height-1 flex align-items-center justify-content-center flex-shrink-0";

export function valueList(value) {
    if (value === null || value === undefined || value === "") return [];
    return Array.isArray(value) ? value.filter(Boolean) : [value];
}

export function tokenPart(value) {
    return String(value || "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export function mentionToken(member) {
    return tokenPart(
        member?.fullname
        || [member?.firstname, member?.lastname].filter(Boolean).join(" ")
        || member?.username
        || `member-${member?.id}`
    );
}

export function referenceTitle(type, item) {
    if (type === "team") return item?.name || item?.title;
    if (type === "customer") return item?.fullname || item?.company || item?.title;
    return item?.title;
}

export function referenceToken(type, item) {
    const title = typeof item === "string" ? item : referenceTitle(type, item);
    return tokenPart(`${type}-${title || item?.documentId || item?.id}`);
}

export function insertAtSelection(value, insertion, selectionStart, selectionEnd) {
    const text = String(value || "");
    const start = Number.isInteger(selectionStart) ? selectionStart : text.length;
    const end = Number.isInteger(selectionEnd) ? selectionEnd : start;
    return {
        value: `${text.slice(0, start)}${insertion}${text.slice(end)}`,
        cursor: start + insertion.length,
    };
}

function entityKey(item) {
    return item?.documentId ?? item?.id;
}

function uniqueItems(items) {
    const seen = new Set();
    return items.filter((item) => {
        if (item?.archivedAt) return false;
        const key = entityKey(item);
        if (key === null || key === undefined || seen.has(String(key))) return false;
        seen.add(String(key));
        return true;
    });
}

export function buildReferenceSuggestions({tasks, projects, goals, teams, customers}, translate = (key) => key) {
    const deliverables = uniqueItems((projects || []).flatMap((project) => project?.deliverables || []));
    const collections = {
        task: uniqueItems(tasks || []),
        project: uniqueItems(projects || []),
        deliverable: deliverables,
        goal: uniqueItems(goals || []),
        team: uniqueItems(teams || []),
        customer: uniqueItems(customers || []),
    };

    return Object.entries(collections).flatMap(([type, items]) => items.map((item) => {
        const config = CHAT_REFERENCE_CONFIG[type];
        const title = referenceTitle(type, item) || translate("inbox.composer.untitled");
        const status = item?.taskStatus
            || item?.projectStatus
            || item?.deliverableStatus
            || item?.goalStatus
            || item?.customerStatus
            || null;
        return {
            suggestionType: "reference",
            type,
            id: item.id,
            documentId: item.documentId,
            title,
            description: item?.description || item?.company || null,
            status,
            insertText: referenceToken(type, item),
            searchText: `${translate(config.labelKey)} ${title} ${richTextToPlain(item?.description)} ${status || ""}`,
        };
    }));
}

function normalizeSearch(value) {
    return String(value || "").toLocaleLowerCase().replace(/\s+/g, "");
}

function ChatComposer({
    value = "",
    members = [],
    references = [],
    referencesLoading = false,
    attachments = [],
    onChange,
    onSelect,
    onRemoveAttachment,
    onAttach,
    onSubmit,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    canAttach = false,
    showQuickActions = true,
    quickActionTypes = QUICK_ACTION_TYPES,
    quickActionsIconOnly = false,
    attachInActionBar = false,
    emojiInActionBar = false,
    submitLabel,
    className = "",
    actionBarClassName = "",
    triggers = ["@", "/"],
    loading = false,
    disabled = false,
    submitDisabled = false,
    // bold / italic / strike / list buttons and Ctrl+B / Ctrl+I (text-formatting.js)
    formatting = true,
}) {
    const translate = useTranslate();
    const inputRef = useRef(null);
    const emojiPanelRef = useRef(null);
    const quickPanelRef = useRef(null);
    const [suggestions, setSuggestions] = useState([]);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [quickType, setQuickType] = useState("task");
    const [quickSearch, setQuickSearch] = useState("");
    const [emojiCategoryId, setEmojiCategoryId] = useState(EMOJI_CATEGORIES[0].id);
    const memberSuggestions = useMemo(() => members.map((member) => ({
        ...member,
        suggestionType: "mention",
        insertText: mentionToken(member),
        searchText: `${member.fullname || ""} ${member.username || ""}`,
    })), [members]);
    const quickSuggestions = useMemo(() => {
        const source = quickType === "mention"
            ? memberSuggestions
            : references.filter((item) => item.type === quickType);
        const needle = normalizeSearch(quickSearch);
        return source.filter((item) => (
            !needle || normalizeSearch(item.searchText || item.title || item.fullname).includes(needle)
        )).slice(0, 30);
    }, [memberSuggestions, quickSearch, quickType, references]);
    const emojiCategory = useMemo(() => (
        EMOJI_CATEGORIES.find((category) => category.id === emojiCategoryId) || EMOJI_CATEGORIES[0]
    ), [emojiCategoryId]);

    const handleSearch = ({trigger, query}) => {
        const source = trigger === "@" ? memberSuggestions : references;
        const needle = normalizeSearch(query);
        setSuggestions(source.filter((item) => (
            !needle || normalizeSearch(item.searchText || item.title || item.fullname).includes(needle)
        )).slice(0, 30));
    };

    const itemTemplate = (suggestion) => {
        if (suggestion.suggestionType === "mention") {
            return (
                <div className="flex align-items-center gap-2 w-full text-xs rtl">
                    <Avatar size="normal" memberData={suggestion}/>
                    <div className="flex flex-column min-w-0">
                        <span className="font-medium overflow-hidden text-overflow-ellipsis white-space-nowrap">
                            {suggestion.fullname || suggestion.username}
                        </span>
                        {suggestion.username && <small className="text-500 text-xs">@{suggestion.username}</small>}
                    </div>
                </div>
            );
        }

        const config = CHAT_REFERENCE_CONFIG[suggestion.type];
        return (
            <div className="flex align-items-center gap-2 w-full text-xs rtl">
                <span className="w-2rem h-2rem border-circle surface-100 flex align-items-center justify-content-center flex-shrink-0">
                    <Icon icon={config.icon} className={`${config.color} text-lg`}/>
                </span>
                <div className="flex flex-column min-w-0 flex-1">
                    <span className="font-medium overflow-hidden text-overflow-ellipsis white-space-nowrap">
                        {suggestion.title}
                    </span>
                    <small className="text-500 text-xs">
                        {translate(config.labelKey)}{suggestion.status ? ` · ${translate(suggestion.status)}` : ""}
                    </small>
                </div>
            </div>
        );
    };

    const emitValue = (nextValue, cursor) => {
        onChange?.({target: {value: nextValue}});
        window.requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(cursor, cursor);
        });
    };

    const insertText = (text) => {
        const result = insertAtSelection(
            value,
            text,
            inputRef.current?.selectionStart,
            inputRef.current?.selectionEnd
        );
        emitValue(result.value, result.cursor);
    };

    const insertEmoji = (emoji) => {
        insertText(emoji);
    };

    const insertSuggestion = (suggestion) => {
        const prefix = suggestion.suggestionType === "mention" ? "@" : "/";
        const cursor = inputRef.current?.selectionStart ?? String(value).length;
        const needsSpace = cursor > 0 && !/\s/.test(String(value).charAt(cursor - 1));
        insertText(`${needsSpace ? " " : ""}${prefix}${suggestion.insertText} `);
        onSelect?.({suggestion});
        quickPanelRef.current?.hide();
    };

    const openQuickPanel = (type, event) => {
        setQuickType(type);
        setQuickSearch("");
        quickPanelRef.current?.show(event);
    };

    const format = (kind) => {
        const element = inputRef.current;
        const result = applyFormat(value, element?.selectionStart, element?.selectionEnd, kind);
        onChange?.({target: {value: result.value}});
        window.requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(result.selectionStart, result.selectionEnd);
        });
    };

    const handleInputKeyDown = (event) => {
        if (overlayVisible) return;
        const shortcut = formatting ? formatShortcut(event) : null;
        if (shortcut) {
            event.preventDefault();
            format(shortcut);
            return;
        }
        if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent?.isComposing && onSubmit) {
            event.preventDefault();
            if (!submitDisabled && !loading && !disabled) onSubmit();
            return;
        }
        onKeyDown?.(event);
    };

    const quickActionConfig = (type) => type === "mention"
        ? {labelKey: "inbox.composer.action.mention", icon: "mi mi:alternate_email", color: "text-primary"}
        : CHAT_REFERENCE_CONFIG[type];

    return (
        <div className={`w-full surface-card border-1 border-200 border-round-xl overflow-hidden ${className}`}>
            <div className="ltr flex align-items-end gap-1 px-2 py-1">
                {canAttach && !attachInActionBar && (
                    <Icon
                        icon="mi mi:attach_file"
                        className={`w-2rem h-2rem text-xl text-500 ${CLICKABLE_ICON_CLASS}`}
                        ariaLabel={translate("inbox.composer.attach-file")}
                        disabled={disabled || loading}
                        onClick={onAttach}
                    />
                )}
                <Mention
                    inputRef={inputRef}
                    value={value}
                    onChange={onChange}
                    suggestions={suggestions}
                    onSearch={handleSearch}
                    onSelect={onSelect}
                    onShow={() => setOverlayVisible(true)}
                    onHide={() => setOverlayVisible(false)}
                    trigger={triggers}
                    field={["insertText", "insertText"]}
                    itemTemplate={itemTemplate}
                    rows={1}
                    autoResize
                    disabled={disabled}
                    placeholder={placeholder || translate("inbox.composer.placeholder")}
                    aria-label={placeholder || translate("inbox.composer.placeholder")}
                    className="flex-1 min-w-0 w-full border-none shadow-none text-xs text-right"
                    panelClassName="rtl w-20rem shabnam-font"
                    scrollHeight="16rem"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    pt={{
                        root: {className: "flex-1 min-w-0"},
                        input: {
                            className: "w-full border-none shadow-none text-xs text-right",
                            style: {
                                minHeight: "2.5rem",
                                maxHeight: "6rem",
                                lineHeight: "1.5rem",
                                overflowY: "auto",
                                resize: "none",
                            },
                            onKeyDown: handleInputKeyDown,
                        },
                        item: {className: "p-2"},
                    }}
                />
                {!emojiInActionBar && (
                    <Icon
                        icon="mi mi:sentiment_satisfied"
                        className={`w-2rem h-2rem text-xl text-500 ${CLICKABLE_ICON_CLASS}`}
                        ariaLabel={translate("inbox.composer.emoji")}
                        disabled={disabled || loading}
                        onClick={(event) => emojiPanelRef.current?.toggle(event)}
                    />
                )}
            </div>

            {!!attachments.length && (
                <div className="rtl flex flex-wrap gap-2 px-2 pb-2">
                    {attachments.map((attachment) => (
                        <span
                            key={entityKey(attachment) || attachment.name}
                            className="flex align-items-center gap-2 surface-100 border-round-xl px-2 py-1 text-xs min-w-0"
                        >
                            <Icon icon="mi mi:attach_file" className="text-sm text-500"/>
                            <span className="max-w-12rem overflow-hidden text-overflow-ellipsis white-space-nowrap">
                                {attachment.name}
                            </span>
                            {onRemoveAttachment && (
                                <Icon
                                    icon="mi mi:close"
                                    className={`w-1rem h-1rem text-sm text-red-500 ${CLICKABLE_ICON_CLASS}`}
                                    ariaLabel={translate("inbox.composer.remove-attachment")}
                                    disabled={disabled || loading}
                                    onClick={() => onRemoveAttachment(attachment)}
                                />
                            )}
                        </span>
                    ))}
                </div>
            )}

            {(showQuickActions || onSubmit) && (
                <div className={`rtl flex flex-wrap align-items-center gap-1 px-2 py-1 border-top-1 border-100 surface-50 ${actionBarClassName}`}>
                    {formatting && <FormatToolbar onFormat={format} disabled={disabled || loading} className="zr-format-group"/>}
                    {canAttach && attachInActionBar && (
                        <Button
                            type="button"
                            text
                            rounded
                            severity="secondary"
                            icon={<Icon icon="mi mi:attach_file" className="text-lg text-500"/>}
                            className="w-2rem h-2rem p-0"
                            aria-label={translate("inbox.composer.attach-file")}
                            disabled={disabled || loading}
                            onClick={onAttach}
                        />
                    )}
                    {showQuickActions && quickActionTypes.map((type) => {
                        const config = quickActionConfig(type);
                        return (
                            <Button
                                key={type}
                                type="button"
                                text
                                severity="secondary"
                                icon={<Icon icon={config.icon} className={`text-sm ${config.color}`}/>}
                                label={quickActionsIconOnly ? undefined : translate(config.labelKey)}
                                rounded={quickActionsIconOnly}
                                className={quickActionsIconOnly ? "w-2rem h-2rem p-0" : "text-xs px-2 py-1 gap-2"}
                                aria-label={translate(config.labelKey)}
                                disabled={disabled || loading}
                                onClick={(event) => openQuickPanel(type, event)}
                            />
                        );
                    })}
                    {emojiInActionBar && (
                        <Button
                            type="button"
                            text
                            rounded
                            severity="secondary"
                            icon={<Icon icon="mi mi:sentiment_satisfied" className="text-lg text-500"/>}
                            className="w-2rem h-2rem p-0"
                            aria-label={translate("inbox.composer.emoji")}
                            disabled={disabled || loading}
                            onClick={(event) => emojiPanelRef.current?.toggle(event)}
                        />
                    )}
                    {onSubmit && (
                        submitLabel ? (
                            <Button
                                type="button"
                                label={submitLabel}
                                icon={<Icon icon={loading ? "mi mi:progress_activity" : "mi mi:send"} className={loading ? "pi-spin" : ""}/>}
                                className="mr-auto text-xs px-4 py-2 gap-2"
                                loading={loading}
                                disabled={submitDisabled || disabled || loading}
                                onClick={onSubmit}
                            />
                        ) : (
                            <Icon
                                icon={loading ? "mi mi:progress_activity" : "mi mi:send"}
                                className={`mr-auto w-2rem h-2rem text-sm text-primary ${CLICKABLE_ICON_CLASS} ${loading ? "pi-spin" : ""}`}
                                ariaLabel={translate("inbox.composer.send")}
                                disabled={submitDisabled || disabled || loading}
                                onClick={onSubmit}
                            />
                        )
                    )}
                </div>
            )}

            <OverlayPanel
                ref={emojiPanelRef}
                className="rtl shabnam-font"
                pt={{content: {className: "p-1"}}}
            >
                <div className="w-20rem" role="group" aria-label={translate("inbox.composer.emoji")}>
                    <div className="ltr flex align-items-center justify-content-between gap-1 overflow-auto p-2 mb-2 border-bottom-1 border-100">
                        {EMOJI_CATEGORIES.map((category) => (
                            <Button
                                key={category.id}
                                type="button"
                                text
                                label={(
                                    <TwemojiText imageClassName="inline-block vertical-align-middle w-1rem h-1rem">
                                        {category.icon}
                                    </TwemojiText>
                                )}
                                className={`w-2rem h-2rem text-lg ${ICON_BUTTON_CLASS} ${emojiCategory.id === category.id ? "surface-200" : ""}`}
                                aria-label={translate(category.labelKey)}
                                aria-pressed={emojiCategory.id === category.id}
                                onClick={() => setEmojiCategoryId(category.id)}
                            />
                        ))}
                    </div>
                    <div className="grid max-h-20rem overflow-auto">
                        {emojiCategory.emojis.map((emoji, index) => (
                            <div key={`${emojiCategory.id}-${emoji}-${index}`} className="col-2 p-1 flex align-items-center justify-content-center">
                                <Button
                                    type="button"
                                    text
                                    className={`w-2rem h-2rem text-xl ${ICON_BUTTON_CLASS}`}
                                    label={(
                                        <TwemojiText imageClassName="inline-block vertical-align-middle w-2rem h-2rem">
                                            {emoji}
                                        </TwemojiText>
                                    )}
                                    aria-label={`${translate("inbox.composer.insert-emoji")} ${emoji}`}
                                    onClick={() => insertEmoji(emoji)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </OverlayPanel>

            <OverlayPanel ref={quickPanelRef} className="rtl shabnam-font w-20rem">
                <div className="flex flex-column gap-2">
                    <InputText
                        value={quickSearch}
                        onChange={(event) => setQuickSearch(event.target.value)}
                        className="w-full text-xs"
                        placeholder={translate("inbox.composer.search")}
                        aria-label={translate("inbox.composer.search")}
                    />
                    <div className="flex flex-column overflow-auto max-h-16rem">
                        {quickSuggestions.length ? quickSuggestions.map((suggestion) => (
                            <button
                                key={`${suggestion.suggestionType}-${suggestion.type || "member"}-${entityKey(suggestion)}`}
                                type="button"
                                className="w-full border-none surface-card hover:surface-100 cursor-pointer text-right p-2 border-round"
                                onClick={() => insertSuggestion(suggestion)}
                            >
                                {itemTemplate(suggestion)}
                            </button>
                        )) : referencesLoading && quickType !== "mention" ? (
                            <span className="text-xs text-500 text-center p-3 flex align-items-center justify-content-center gap-2">
                                <i className="pi pi-spin pi-spinner"/>
                                در حال دریافت موارد...
                            </span>
                        ) : (
                            <span className="text-xs text-500 text-center p-3">
                                {translate("inbox.composer.no-results")}
                            </span>
                        )}
                    </div>
                </div>
            </OverlayPanel>
        </div>
    );
}

export default ChatComposer;
